import { useEffect, useState } from 'react';
import { signInWithCustomToken } from 'firebase/auth';
import { Loader2, AlertCircle } from 'lucide-react';
import { auth } from '@/config/firebase';

/**
 * Login-ensure handoff page — used as the first load of OAuth popups (and
 * any other flow where the new window must continue on behalf of a specific
 * user even when its own storage bucket lacks auth state, e.g. partitioned
 * iframe contexts, Safari ITP).
 *
 * Reads a one-time custom token + destination from the URL fragment, signs
 * the window in, and redirects. Using the hash keeps the token out of
 * server logs and the referer header.
 *
 * Expected hash params:
 *   _ua_sso_token  — short-lived Firebase custom token (may be absent if
 *                    minting failed — we fall back to whatever session the
 *                    window's IndexedDB already holds)
 *   _ua_sso_dest   — the destination URL to forward to after sign-in
 *   _ua_sso_uid    — the UID the opener expected; used to catch stale
 *                    cached sessions in the window's storage bucket
 */
export function LoginEnsurePage() {
  const [error, setError] = useState<string | null>(null);
  const [dest, setDest] = useState<string>('');

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const token = params.get('_ua_sso_token') || '';
      const destUrl = params.get('_ua_sso_dest') || '';
      const expectedUid = params.get('_ua_sso_uid') || '';
      setDest(destUrl);

      if (!destUrl) {
        setError('Missing destination URL.');
        return;
      }

      try {
        await auth.authStateReady();

        if (token) {
          // Sign in with the one-time custom token — idempotent for the same
          // user, correctly replaces a stale session otherwise.
          await signInWithCustomToken(auth, token);
          // Guardrail: with a token, we know exactly who we signed in as. If
          // the resulting UID doesn't match what the opener expected, refuse
          // to proceed — something is wrong and we'd write OAuth tokens to
          // the wrong user's Firestore doc.
          if (expectedUid && auth.currentUser?.uid !== expectedUid) {
            setError('Signed-in user does not match. Please sign out and try again.');
            return;
          }
        }
        // No token: degrade gracefully to the pre-existing flow. The OAuth
        // provider will redirect to /oauth-home, which prompts for sign-in
        // when needed. No UID check — same risk profile as before.

        window.location.replace(destUrl);
      } catch (err) {
        console.error('[LoginEnsure] pre-auth failed', err);
        setError(err instanceof Error ? err.message : 'Pre-auth failed');
      }
    })();
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="text-center">
        {error ? (
          <>
            <AlertCircle className="mx-auto h-8 w-8 text-destructive mb-3" />
            <p className="text-sm font-medium text-destructive">{error}</p>
            {dest && (
              <a href={dest} className="mt-3 inline-block text-sm text-primary hover:underline">
                Continue manually
              </a>
            )}
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Connecting...</p>
          </>
        )}
      </div>
    </div>
  );
}
